import React, { ReactNode, Suspense, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { URLParams } from '../common/types';
import WholeScreenLoading from '../components/whole-screen-loading/WholeScreenLoading';
import { NotFoundErrorPage } from '../pages/errors/NotFoundErrorPage';
import { analytics, restaurantMiddlewareBuilder } from '../helpers/analytics';
import { useStatsigUser } from '@statsig/react-bindings';
import { validateAndCorrectLanguage } from '../i18n';
import { emitSessionBootstrap } from '../helpers/gtm';
import { emitPageView } from '../helpers/gtm/surface/events';
import { toISO4217 } from '../helpers/currency';
import { useRestaurantBySlugSuspense } from '../api/getRestaurantBySlug';
import { useFeatureFlagsSuspense } from '../api/getRestaurantFeatureFlags';
import { useTablesSuspense } from '../api/getRestaurantTables';
import { useEventsByRestaurant } from '../api/getEventsByRestaurant';
import { usePrefetchAvailability } from '../hooks/usePrefetchAvailability';

export interface RestaurantGuardOutletContext {
	restaurant: TDR.EnrichedRestaurant;
	featureFlags: TDR.FeatureFlags;
	tables: TDR.Table[];
}
export interface RestaurantGuardProps {
	children: ReactNode;
}

export const RestaurantGuardContext = React.createContext<RestaurantGuardOutletContext | undefined>(undefined);

/**
 * Inner component that uses Suspense queries
 * Separated to ensure proper Suspense boundaries
 */
const RestaurantGuardContent = React.memo(({
	restaurantSlug,
	children
}: {
	restaurantSlug: string;
	children: ReactNode;
}) => {
	const { updateUserAsync } = useStatsigUser();
	const location = useLocation();
	const analyticsInitialized = useRef(false);

	// First query: Get restaurant data (suspends on first load)
	const { data: restaurant } = useRestaurantBySlugSuspense({ slug: restaurantSlug });

	// Handle 404 case
	if (!restaurant) {
		return <NotFoundErrorPage />;
	}

	// Second layer: Dependent queries that load in parallel (suspends on first load)
	const { data: featureFlags } = useFeatureFlagsSuspense(restaurant.id);
	const { data: tables } = useTablesSuspense(restaurant.id, false);

	// Events are fetched non-suspending so the page renders immediately; we only
	// need the list to kick off per-event availability prefetching in the background.
	const { data: events } = useEventsByRestaurant(restaurant.id);

	// Proactively prefetch availability data in background.
	// Passing `events` causes per-event calendar + timeslot data to be cached
	// before the user reaches the carousel, so Book Now buttons render ready.
	usePrefetchAvailability(restaurant, tables, events);

	// Track page views on route changes only (not search param updates).
	// emitSessionBootstrap is called first so GA4 session attribution picks up
	// the landing URL (with UTMs intact) before the first page_view event.
	useEffect(() => {
		if (restaurant) {
			emitSessionBootstrap(restaurant.id);
			emitPageView(
				{
					venueId: restaurant.id,
					venueName: restaurant.name,
					currency: toISO4217(restaurant.currency) ?? ''
				},
				{ path: location.pathname, title: document.title }
			);
		}
	}, [location.pathname, restaurant?.id]);

	// Analytics and language setup - runs only once per restaurant
	useEffect(() => {
		if (restaurant && !analyticsInitialized.current) {
			const trackingMiddleware = restaurantMiddlewareBuilder(restaurant.id, restaurant.name);
			analytics.addSourceMiddleware(trackingMiddleware);
			analyticsInitialized.current = true;

			updateUserAsync({
				customIDs: {
					restaurantID: restaurant.id.toString(),
					restaurantSlug: restaurant.slug
				},
				custom: {
					restaurantCity: restaurant.city,
					restaurantRegion: restaurant.region,
					restaurantCountry: restaurant.country
				},
				appVersion: process.env.RELEASE_VERSION
			});

			validateAndCorrectLanguage(restaurant.supportedLocales);

			return () => {
				analytics.removeSourceMiddleware(trackingMiddleware);
				analyticsInitialized.current = false;
			};
		}
	}, [restaurant, updateUserAsync]);

	// Memoize context value to prevent unnecessary re-renders of consumers
	const contextValue = useMemo(
		() => ({ restaurant, featureFlags, tables }),
		[restaurant, featureFlags, tables]
	);

	return (
		<RestaurantGuardContext.Provider value={contextValue}>
			{children}
		</RestaurantGuardContext.Provider>
	);
});

RestaurantGuardContent.displayName = 'RestaurantGuardContent';

/**
 * RestaurantGuard with Suspense boundary
 * Optimized for performance as a top-level route component:
 * - Uses Suspense for automatic loading states
 * - Memoized context value to prevent cascade re-renders
 * - Parallel loading of dependent queries (featureFlags + tables)
 * - Aggressive caching (5 min staleTime, 10 min gcTime)
 * - No refetch on mount/focus for better UX
 */
export const RestaurantGuard = ({ children }: RestaurantGuardProps) => {
	const { restaurantSlug } = useParams<URLParams>();

	// Don't render if no slug (shouldn't happen in normal routing)
	if (!restaurantSlug) {
		return <NotFoundErrorPage />;
	}

	return (
		<Suspense fallback={<WholeScreenLoading id='main-loading-page' />}>
			<RestaurantGuardContent restaurantSlug={restaurantSlug}>
				{children}
			</RestaurantGuardContent>
		</Suspense>
	);
};

/**
 * Custom hook to consume RestaurantGuardContext
 * @throws {Error} if used outside RestaurantGuard
 */
export const useRestaurantGuardContext = (): RestaurantGuardOutletContext => {
	const context = React.useContext(RestaurantGuardContext);
	if (!context) {
		throw new Error('useRestaurantGuardContext must be used within RestaurantGuard');
	}
	return context;
};
